import { WorkflowType } from '@kindo/universal';
import { useRouter } from 'next/router';

import {
  Button,
  ButtonType,
  Icon,
  Size,
  TextField,
  TextFieldType,
  TextIconColor,
  TypographyFont,
  TypographySize,
  TypographyWeight
} from '~/components/core';
import { Badge } from '~/components/core/Badge';
import { WORKFLOW_TYPE_TO_LABEL } from '~/constants';

interface WorkflowViewLayoutProps {
  children: React.ReactElement | React.ReactElement[];
  headerChildren?: React.ReactElement[] | undefined;
  name?: string | undefined;
  onBack?: () => void;
  onNameBlur?: () => void;
  onNameChange?: (title: string) => void;
  workflowType?: WorkflowType | undefined;
}

const WorkflowViewLayout = ({
  children,
  workflowType = WorkflowType.MANUAL,
  onNameChange,
  onNameBlur,
  name,
  onBack,
  headerChildren
}: WorkflowViewLayoutProps) => {
  const router = useRouter();
  const defaultOnBack = () => {
    void router.back();
  };
  return (
    <div
      className="flex h-full flex-col gap-4 overflow-hidden p-[1px] py-2" // 1px padding needed to show darkGlowWorkflowCardContainerStyles border
      data-testid="workflow-view-layout-container"
    >
      <div
        className="flex flex-shrink-0 flex-col gap-3"
        data-testid="header-container"
      >
        <div
          className="flex flex-shrink-0 flex-row gap-1"
          data-testid="header-row-container"
        >
          <Badge active={false} label={WORKFLOW_TYPE_TO_LABEL[workflowType]} />
          <Button
            label="back"
            onClick={onBack || defaultOnBack}
            size={Size.SMALL}
            startIcon={Icon.CHEVRON_LEFT}
            textIconColor={TextIconColor.HIGHLIGHT}
            type={ButtonType.TEXT}
          />
        </div>
        <div
          className="flex flex-row justify-between gap-3"
          data-testid="title-and-actions-container"
        >
          <div
            className="flex w-full flex-row gap-1 border-b-[0.5px] border-white/10 pb-1"
            data-testid="title-container"
          >
            <TextField
              disabled={!onNameChange}
              font={TypographyFont.HEADING}
              fullWidth
              onBlur={onNameBlur}
              onChange={onNameChange ?? (() => {})}
              placeholder={`${WORKFLOW_TYPE_TO_LABEL[workflowType]} Agent`}
              showEndIcon={false}
              size={TypographySize.H3}
              startIcon={onNameChange ? Icon.EDIT : undefined}
              startIconFocusColor={TextIconColor.HIGHLIGHT}
              textWeight={TypographyWeight.MEDIUM}
              type={TextFieldType.SIMPLE}
              value={name ?? ''}
            />
          </div>
          <div
            className="flex flex-row items-end gap-2"
            data-testid="actions-container"
          >
            {headerChildren}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

export default WorkflowViewLayout;
